import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle, XCircle, MinusCircle, RotateCcw, AlertCircle } from 'lucide-react';
import { api, formatDate, formatDateTime, downloadCsv } from '../../lib/api';
import { StatusBadge, TIME_NOTE, fraction, minutes } from '../../components/admin/EngineProgress';

const EVENT_LABEL = {
  lesson_started: 'Started the lesson',
  lesson_completed: 'Completed the lesson',
  block_viewed: 'Viewed',
  video_progress: 'Watched video',
  video_completed: 'Finished video',
  knowledge_check_answered: 'Answered',
  continue_unlocked: 'Unlocked Continue',
  block_unlocked: 'Unlocked the rest of the lesson',
  quiz_submitted: 'Submitted quiz',
  audio_progress: 'Listened to audio',
  audio_completed: 'Finished audio',
  scenario_choice: 'Made a scenario choice',
  video_branch_chosen: 'Chose a video path',
  quiz_attempts_reset: 'Attempts reset',
};

const RESET_ERROR = {
  already_passed: 'The learner has already passed this quiz.',
  no_attempts: 'The learner hasn’t used any attempts.',
  unlimited_attempts: 'This quiz has no attempt limit.',
  quiz_not_found: 'This quiz isn’t in the learner’s version of the course.',
};

function describe(ev) {
  if (ev.name === 'video_progress' || ev.name === 'audio_progress') return `${ev.data?.percent ?? '?'}%`;
  if (ev.name === 'knowledge_check_answered') return `attempt ${ev.data?.attempt ?? '?'}, ${ev.data?.correct ? 'correct' : 'incorrect'}`;
  if (ev.name === 'quiz_submitted') return `attempt ${ev.data?.attempt ?? '?'}, ${ev.data?.score ?? '?'}%${ev.data?.graded ? (ev.data?.passed ? ', passed' : ', not passed') : ', practice'}`;
  if (ev.name === 'scenario_choice' || ev.name === 'video_branch_chosen') return `option ${(ev.data?.choice ?? ev.data?.option ?? 0) + 1}`;
  if (ev.name === 'quiz_attempts_reset') {
    const n = ev.data?.previous_attempts;
    return `by ${ev.data?.by_name || ev.data?.by_email || 'an admin'}${n != null ? `, ${n} attempt${n === 1 ? '' : 's'} cleared` : ''}`;
  }
  return '';
}

// Yes / no / not applicable, always with text so it doesn't rely on color.
function Mark({ value, yes, no }) {
  if (value == null) return <span className="text-slate-500">—</span>;
  return value ? (
    <span className="inline-flex items-center gap-1 text-emerald-800">
      <CheckCircle size={14} aria-hidden="true" /> {yes}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-slate-600">
      <MinusCircle size={14} aria-hidden="true" /> {no}
    </span>
  );
}

// Gives the learner a graded quiz's full allowance again, after a confirm step.
function ResetAttempts({ b, learnerName, onReset }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef(null);
  const startRef = useRef(null);
  const max = b.quiz.max_attempts;

  useEffect(() => {
    if (confirming) confirmRef.current?.focus();
  }, [confirming]);

  const cancel = () => {
    setConfirming(false);
    setTimeout(() => startRef.current?.focus(), 0);
  };
  const confirm = async () => {
    setBusy(true);
    await onReset(b);
    setBusy(false);
    setConfirming(false);
  };

  if (!confirming) {
    return (
      <button
        ref={startRef}
        type="button"
        onClick={() => setConfirming(true)}
        aria-label={`Reset attempts for ${b.quiz.title || 'this quiz'}`}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold hover:bg-slate-50"
      >
        <RotateCcw size={13} aria-hidden="true" /> Reset attempts
      </button>
    );
  }
  return (
    <div role="group" aria-label={`Confirm resetting attempts for ${b.quiz.title || 'this quiz'}`} className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-950">
      <p>
        Give {learnerName} all {max} attempt{max === 1 ? '' : 's'} again? Their best score stays on record.
      </p>
      <div className="mt-2 flex gap-2">
        <button ref={confirmRef} type="button" onClick={confirm} disabled={busy} className="rounded-lg bg-slate-900 px-2.5 py-1 font-semibold text-white disabled:opacity-40">
          {busy ? 'Resetting…' : 'Reset attempts'}
        </button>
        <button type="button" onClick={cancel} disabled={busy} className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 font-semibold hover:bg-slate-50 disabled:opacity-40">
          Cancel
        </button>
      </div>
    </div>
  );
}

// The reset button goes away (or the confirm closes), so focus moves here.
function ResetMessage({ message }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
  }, [message]);
  return (
    <p ref={ref} tabIndex={-1} role={message.error ? 'alert' : 'status'} className={`flex items-center gap-1 text-xs outline-none ${message.error ? 'text-rose-800' : 'text-emerald-800'}`}>
      {message.error ? <AlertCircle size={13} aria-hidden="true" /> : <CheckCircle size={13} aria-hidden="true" />} {message.text}
    </p>
  );
}

function QuizCell({ b, learnerName, onReset, message }) {
  if (b.quiz) {
    const canReset = b.quiz.graded && b.quiz.max_attempts != null && b.quiz.attempts > 0 && !b.quiz.passed;
    const status = message ? <ResetMessage message={message} /> : null;
    if (!b.quiz.attempts && !b.quiz.attempts_total) {
      return (
        <div className="space-y-1">
          <span className="text-slate-600">Not taken</span>
          {status}
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <p>
          {b.quiz.attempts}
          {b.quiz.max_attempts != null ? ` of ${b.quiz.max_attempts}` : ''} attempt{(b.quiz.max_attempts ?? b.quiz.attempts) === 1 ? '' : 's'}
          {b.quiz.resets?.length ? ' since reset' : ''} · best {b.quiz.best_score ?? '—'}%
          {b.quiz.graded ? (b.quiz.passed ? ' · passed' : ' · not passed') : ' · practice'}
        </p>
        <ol className="list-decimal pl-5 text-xs text-slate-600">
          {(b.answers || []).map((a, i) => (
            <li key={i}>{a.text || '(no answers)'}</li>
          ))}
        </ol>
        {(b.quiz.resets || []).map((r, i) => (
          <p key={i} className="inline-flex items-center gap-1 text-xs text-slate-700">
            <RotateCcw size={12} aria-hidden="true" /> Attempts reset by {r.by} · {formatDateTime(r.at)}
            {r.previous_attempts != null ? ` (${r.previous_attempts} used before)` : ''}
          </p>
        ))}
        {canReset ? <ResetAttempts b={b} learnerName={learnerName} onReset={onReset} /> : null}
        {status}
      </div>
    );
  }
  if (!b.kc) return <span className="text-slate-500">—</span>;
  if (!b.attempts) return <span className="text-slate-600">Not answered</span>;
  return (
    <div className="space-y-1">
      <p>
        {b.attempts} attempt{b.attempts === 1 ? '' : 's'} ·{' '}
        {b.correct_first_try ? (
          <span className="inline-flex items-center gap-1 text-emerald-800">
            <CheckCircle size={13} aria-hidden="true" /> right first try
          </span>
        ) : b.answered_correctly ? (
          'got it right later'
        ) : (
          <span className="inline-flex items-center gap-1 text-rose-800">
            <XCircle size={13} aria-hidden="true" /> not right yet
          </span>
        )}
      </p>
      <ol className="list-decimal pl-5 text-xs text-slate-600">
        {(b.answers || []).map((a, i) => (
          <li key={i}>
            {a.text || '(nothing)'} — {a.correct ? 'correct' : 'incorrect'}
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function AdminLearnerProgress() {
  const { id: courseId, enrollmentId } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState({});

  const load = useCallback(
    () =>
      api(`/api/admin/course-engine/enrollments/${enrollmentId}/progress`)
        .then(setD)
        .catch((e) => setError(e.message)),
    [enrollmentId],
  );
  useEffect(() => {
    load();
  }, [load]);

  const resetQuiz = async (lessonId, b) => {
    const key = `${lessonId}:${b.id}`;
    try {
      const r = await api(`/api/admin/course-engine/enrollments/${enrollmentId}/quiz-reset`, { method: 'POST', body: { lesson_id: lessonId, block_id: b.id } });
      await load();
      setMessages((m) => ({ ...m, [key]: { text: `Attempts reset. The learner has all ${r.max_attempts} again.` } }));
    } catch (err) {
      setMessages((m) => ({ ...m, [key]: { error: true, text: RESET_ERROR[err.message] || `Couldn’t reset attempts: ${err.message}` } }));
    }
  };

  if (error) return <div className="p-8 text-rose-700">{error}</div>;
  if (!d) return <div className="p-8 text-slate-600">Loading…</div>;

  const { learner, progress, version } = d;
  const t = progress?.totals;

  const exportTimeline = () =>
    downloadCsv(
      d.timeline.map((ev) => ({ when: ev.at, event: EVENT_LABEL[ev.name] ?? ev.name, lesson: ev.lesson, block: ev.block ?? '', detail: describe(ev) })),
      `${learner.name || learner.email} - ${d.course.title} - activity.csv`,
      [[`Learner: ${learner.name ?? ''} <${learner.email}>`], [`Course: ${d.course.title}${version ? ` (v${version.number})` : ''}`]],
    );

  return (
    <div className="p-4 lg:p-8 max-w-[1200px] mx-auto">
      <Link to={`/admin/courses/${courseId}/reports?tab=progress`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
        <ArrowLeft size={14} /> Back to learner progress
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{learner.name || learner.email}</h1>
          <p className="text-sm text-slate-600 mt-1">
            <Link to={`/admin/users/${learner.user_id}`} className="hover:underline">
              {learner.email}
            </Link>{' '}
            · {d.course.title}
            {version ? ` · version ${version.number}${version.pinned ? '' : ' (not opened yet)'}` : ''}
          </p>
        </div>
        <StatusBadge status={learner.status} />
      </div>

      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          ['Lessons', fraction(t?.lessons)],
          ['Activities', fraction(t?.activities)],
          ['Blocks seen', fraction(t?.seen)],
          ['Knowledge checks', t?.kc.total ? `${t.kc.answered}/${t.kc.total} (${t.kc.first_try_correct} right first try)` : '—'],
          ...(t?.quizzes?.total ? [['Graded quizzes', `${t.quizzes.passed}/${t.quizzes.total} passed${t.quizzes.score != null ? ` · ${t.quizzes.score}%` : ''}`]] : []),
          ['Time (est.)', learner.status === 'not_started' ? '—' : minutes(t?.active_minutes)],
          ['Enrolled', formatDate(learner.enrolled_at)],
          ['Completed', learner.completed_at ? formatDate(learner.completed_at) : '—'],
          ['Certificate', learner.certificate_number || '—'],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white p-3">
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-600">{k}</dt>
            <dd className="mt-1 font-semibold">{v}</dd>
          </div>
        ))}
      </dl>

      {(progress?.lessons || []).map((lesson, i) => (
        <section key={lesson.id} className="mb-6 bg-white rounded-lg border border-slate-200 overflow-hidden" aria-labelledby={`lesson-${i}`}>
          <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
            <h2 id={`lesson-${i}`} className="font-semibold">
              {lesson.title}
            </h2>
            <span className="text-sm text-slate-600">
              {lesson.completed ? 'Completed' : lesson.started ? 'In progress' : 'Not started'} · activities {fraction(lesson.activities)} · seen {fraction(lesson.seen)}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-600 bg-slate-50">
                <tr>
                  <th className="px-4 py-2 w-[40%]">Block</th>
                  <th className="px-4 py-2">Seen</th>
                  <th className="px-4 py-2">Activity</th>
                  <th className="px-4 py-2">Quiz</th>
                </tr>
              </thead>
              <tbody>
                {lesson.blocks
                  .filter((b) => b.viewable || b.interactive)
                  .map((b) => (
                    <tr key={b.id} className="border-t border-slate-100 align-top">
                      <td className="px-4 py-2">
                        <span className="font-medium">{b.label}</span>
                        {b.inColumn ? <span className="ml-1 text-xs text-slate-600">(in a column)</span> : null}
                        {b.summary ? <span className="block text-xs text-slate-600">{b.summary}</span> : null}
                      </td>
                      <td className="px-4 py-2">
                        <Mark value={b.viewable ? b.viewed : null} yes="Seen" no="Not seen" />
                      </td>
                      <td className="px-4 py-2">
                        <Mark value={b.completed} yes="Completed" no="Not yet" />
                      </td>
                      <td className="px-4 py-2">
                        <QuizCell
                          b={b}
                          learnerName={learner.name || learner.email}
                          onReset={(blk) => resetQuiz(lesson.id, blk)}
                          message={messages[`${lesson.id}:${b.id}`]}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <section className="bg-white rounded-lg border border-slate-200 overflow-hidden" aria-labelledby="timeline-h">
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <h2 id="timeline-h" className="font-semibold">
            Activity timeline
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-600">
              {d.timeline.length < d.timeline_total ? `Latest ${d.timeline.length} of ${d.timeline_total}` : `${d.timeline_total} events`}
            </span>
            <button type="button" onClick={exportTimeline} disabled={!d.timeline.length} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-40">
              <Download size={14} /> Timeline CSV
            </button>
          </div>
        </div>
        {d.timeline.length ? (
          <div className="overflow-x-auto max-h-[32rem]" tabIndex={0} role="region" aria-label="Activity timeline events">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-600 bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">What</th>
                  <th className="px-4 py-2">Lesson</th>
                  <th className="px-4 py-2">Block</th>
                </tr>
              </thead>
              <tbody>
                {d.timeline.map((ev, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-1.5 whitespace-nowrap text-slate-600">{formatDateTime(ev.at)}</td>
                    <td className="px-4 py-1.5">
                      {EVENT_LABEL[ev.name] ?? ev.name}
                      {describe(ev) ? <span className="text-slate-600"> · {describe(ev)}</span> : null}
                    </td>
                    <td className="px-4 py-1.5 text-slate-600">{ev.lesson}</td>
                    <td className="px-4 py-1.5 text-slate-600">{ev.block ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="p-5 text-sm text-slate-600">No activity yet.</p>
        )}
        <p className="px-5 py-3 border-t border-slate-100 text-xs text-slate-600">{TIME_NOTE}</p>
      </section>
    </div>
  );
}
